import React from 'react';
import PageShell from '../../components/PageShell';
import { SectionHeading, GradientButton, CTABanner, StepChip } from '../../components/common/Primitives';
import { RESEARCHER_ACTIVITIES } from '../../lib/mockResearcher';
import { ArrowRight } from 'lucide-react';

const toneMap = {
  purple: 'bg-purple-50 text-purple ring-purple/20',
  royal:  'bg-royal-50 text-royal ring-royal/20',
  cyan:   'bg-cyan-50 text-cyan ring-cyan/20',
  mint:   'bg-mint-50 text-mint-600 ring-mint/20',
};

export default function ResearcherActivities() {
  const d = RESEARCHER_ACTIVITIES;
  return (
    <PageShell>
      {/* HERO */}
      <section className="px-6 pt-12 pb-8" data-testid="researcher-activities-hero">
        <div className="mx-auto max-w-[1240px]">
          <div className="inline-flex items-center rounded-full bg-purple-50 px-3 py-1 text-[11px] font-bold tracking-widest text-purple">{d.hero.eyebrow}</div>
          <h1 className="mt-3 text-[40px] md:text-[50px] font-extrabold leading-[1.05] tracking-tight text-navy">{d.hero.title}</h1>
          <p className="mt-5 max-w-2xl text-[15.5px] leading-relaxed text-ink-500">{d.hero.desc}</p>
          <div className="mt-6 rounded-2xl bg-gradient-to-r from-purple-50 via-cyan-50 to-mint-50 p-5 shadow-soft ring-1 ring-purple/20">
            <div className="text-[16px] font-extrabold text-navy tracking-widest">{d.hero.flowLabel}</div>
            <p className="mt-1 text-[13px] text-ink-500">{d.hero.flowDesc}</p>
          </div>
          <div className="mt-6">
            <GradientButton to={d.hero.cta.to}>{d.hero.cta.label} <ArrowRight className="h-4 w-4" /></GradientButton>
          </div>
        </div>
      </section>

      {/* RESEARCH PROGRAMME */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.programme.eyebrow} title={d.programme.title} desc={d.programme.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {d.programme.streams.map((s, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
                <div className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-bold tracking-widest ring-1 ${toneMap[s.color]}`}>{s.n}</div>
                <h3 className="mt-4 text-[15.5px] font-bold text-navy">{s.title}</h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-ink-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EVALUATION FOCUS */}
      <section className="px-6 py-16 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.focus.eyebrow} title={d.focus.title} desc={d.focus.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {d.focus.items.map((f, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
                <div className={`inline-flex h-11 w-11 items-center justify-center rounded-xl text-[13px] font-bold ${toneMap[f.color]}`}>{f.key}</div>
                <h3 className="mt-3 text-[15px] font-bold text-navy">{f.title}</h3>
                <p className="mt-1.5 text-[12.5px] leading-relaxed text-ink-500">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EVIDENCE STATUS */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.status.eyebrow} title={d.status.title} desc={d.status.desc} />
          <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {d.status.rows.map((r, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
                <div className={`inline-flex items-center rounded-full px-3 py-1 text-[10.5px] font-bold tracking-widest ring-1 ${toneMap[r.tone]}`}>{r.label}</div>
                <p className="mt-3 text-[13.5px] leading-relaxed text-ink-500">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RESEARCH LIFECYCLE */}
      <section className="px-6 py-16 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.lifecycle.eyebrow} title={d.lifecycle.title} desc={d.lifecycle.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {d.lifecycle.steps.map((s) => (
              <div key={s.n} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
                <StepChip n={s.n} color={s.color} />
                <h3 className="mt-4 text-[14.5px] font-bold text-navy">{s.title}</h3>
                <p className="mt-1 text-[12.5px] leading-relaxed text-ink-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RESOURCES */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.resources.eyebrow} title={d.resources.title} desc={d.resources.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {d.resources.items.map((r, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
                <div className={`inline-flex h-11 w-11 items-center justify-center rounded-xl text-[16px] font-extrabold ${toneMap[r.color]}`}>{r.key}</div>
                <h3 className="mt-3 text-[15px] font-bold text-navy">{r.title}</h3>
                <p className="mt-1 text-[12.5px] leading-relaxed text-ink-500">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 pb-24">
        <div className="mx-auto max-w-[1240px]">
          <CTABanner title={d.cta.title} primary={d.cta.primary} />
        </div>
      </section>
    </PageShell>
  );
}
